// Revisión automática de una interacción del agente (Actividad). Lee la traza guardada por el motor y señala, en lenguaje claro,
// lo que conviene mirar primero cuando una respuesta salió mal: fallos, cortes, herramientas sin datos y cifras que el agente
// dijo pero que no salen de ninguna fuente (posible invención). Son pistas, no veredictos: cada una dice dónde mirar.

const EMPTY_OUTPUT = /^\s*(\[\s*\]|\{\s*\}|null|undefined|""|)\s*$|sin resultados|no (se )?encontr|no hay (datos|resultados|registros)|0 (resultados|registros|filas)/i;

const numbers = (text) => {
  const out = new Set();
  for (const m of String(text || '').matchAll(/\d+(?:[.,]\d+)*/g)) {
    const digits = m[0].replace(/[.,]/g, '');
    if (digits.length >= 2) out.add(digits);
    const whole = m[0].replace(/[.,]0+$/, '').replace(/[.,]/g, '');   // 1,299.00 y 1,299 son la misma cifra
    if (whole.length >= 2) out.add(whole);
  }
  return out;
};

const join = (list) => list.join(', ');
const short = (s, n = 160) => { const t = String(s || '').replace(/\s+/g, ' ').trim(); return t.length > n ? `${t.slice(0, n)}…` : t; };

/** Todo lo que el agente tenía a la vista: de aquí sí pueden salir los datos de su respuesta. */
export function sources(t) {
  const parts = [t.input];
  for (const s of t.steps || []) {
    if (s.kind === 'context') {
      parts.push(s.system, s.preData);
      (s.history || []).forEach((h) => parts.push(h.text));
      (s.memory || []).forEach((m) => parts.push(m));
      (s.knowledge || []).forEach((k) => parts.push(k.text));
    } else if (s.kind === 'tool') parts.push(s.output, s.args);
  }
  return parts.filter(Boolean).join('\n');
}

/** @returns {{ level: 'error'|'warn'|'info', title: string, detail: string }[]} del más grave al menos grave. */
export function diagnose(t) {
  const steps = Array.isArray(t?.steps) ? t.steps : [];
  const out = [];
  const add = (level, title, detail) => out.push({ level, title, detail });
  const ctx = steps.find((s) => s.kind === 'context');
  const models = steps.filter((s) => s.kind === 'model');
  const tools = steps.filter((s) => s.kind === 'tool');
  const answered = t?.status === 'ok' && Boolean(t.output);

  for (const s of steps.filter((x) => x.kind === 'error')) add('error', 'La corrida falló', `${s.text} Busca el paso rojo en la línea de tiempo.`);
  if (t?.status === 'ignored' && t.reason) add('info', 'El agente no respondió', t.reason);
  if (t?.attempt > 1) add('info', `Es el intento ${t.attempt}`, 'El motor reintentó porque un intento anterior falló. Revisa la actividad del mismo mensaje.');

  for (const m of models) {
    if (m.finish === 'length') add('warn', 'La respuesta del modelo se cortó', `Llegó al límite de palabras permitido${ctx?.params?.maxTokens ? ` (${ctx.params.maxTokens} tokens)` : ''}. Pídele en las instrucciones respuestas más cortas, o pide subir ese límite al equipo técnico.`);
    else if (m.finish === 'content_filter') add('warn', 'OpenAI filtró la respuesta', 'El modelo no pudo terminar por sus filtros de contenido.');
  }

  for (const s of tools) {
    const where = s.pre ? 'Datos previos' : 'Herramienta';
    const name = s.name.replace(/^antes de responder: /, '');
    if (!s.ok) {
      add('error', `${where} «${name}» falló`, `${s.error || 'Sin detalle.'}${s.pre ? ' El agente respondió sin esos datos.' : answered ? ' Aun así el agente respondió: confirma que no inventó lo que la herramienta debía darle.' : ''}`);
    } else if (EMPTY_OUTPUT.test(String(s.output || '')) || EMPTY_OUTPUT.test(short(s.output, 80))) {
      add('warn', `${where} «${name}» no devolvió datos`, 'Funcionó, pero sin resultados. Si el agente contestó con datos concretos, vienen de otro lado: ábrela y compara.');
    }
    if (s.clipped) add('info', `La salida de «${name}» se guardó recortada`, 'Era más larga de lo que guarda la actividad; el modelo sí la recibió completa.');
  }

  if (answered) {
    const known = numbers(sources(t));
    const loose = [...numbers(t.output)].filter((n) => !known.has(n));
    if (loose.length) {
      const offered = (ctx?.tools || []).filter((x) => x !== 'request_human_help');
      const noTool = offered.length && !tools.some((s) => !s.pre);
      add('warn', 'Posible invención: cifras sin origen', `La respuesta menciona ${join(loose.slice(0, 6))}${loose.length > 6 ? '…' : ''}, que no aparecen en las instrucciones, el historial, el conocimiento ni lo que devolvieron las herramientas.${noTool ? ` Tenía herramientas (${join(offered.slice(0, 4))}) y no usó ninguna.` : ''} Puede ser un cálculo legítimo: compruébalo.`);
    }
  }

  const found = ctx?.knowledge || [];
  if (found.length && !found.some((k) => k.used)) add('info', 'Había conocimiento, pero muy poco parecido', 'Se encontraron fragmentos y ninguno pasó el mínimo de parecido, así que el agente respondió sin ellos.');

  for (const s of steps.filter((x) => x.kind === 'note')) {
    if (/no disponible|no aceptó|no se pudo|no escucha|tope de gasto|respaldo|Instrucciones:/i.test(s.text)) add('warn', 'Aviso del motor', s.text);
  }

  if (ctx?.clipped || steps.some((s) => s.kind === 'note' && /demasiado grande|muy grande/.test(s.text))) add('info', 'Parte de la traza se guardó recortada', 'Los textos muy largos se recortan al guardar la actividad; el modelo sí recibió todo.');
  const slow = models.filter((m) => m.ms > 10000);
  if (slow.length) add('info', 'El modelo tardó mucho', `${slow.length === 1 ? 'Una vuelta tardó' : `${slow.length} vueltas tardaron`} más de 10 segundos.`);

  const rank = { error: 0, warn: 1, info: 2 };
  return out.sort((a, b) => rank[a.level] - rank[b.level]);
}
